"use client";

import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import React, { useState } from "react";

import ebook from "@/public/images/book/survival-guide-drjohn-watts1.jpg";

declare let grecaptcha: any;

export default function EbookDownload() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    suffering: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, token, formType: "ebook" }),
        });

        if (response.ok) {
          const responseData = await response.json();
          setSuccessMessage(responseData.message);
          setSubmitted(true);
          setErrorMessage("");
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
        setSuccessMessage("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      city: "",
      suffering: "",
    });
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <section className="bg-white bg-fixed py-20">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <div className="mx-auto flex h-auto max-w-6xl flex-col gap-8 md:h-full md:flex-row">
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 px-6 text-black md:w-1/2 md:px-0">
          {submitted ? (
            <div className="text-center">
              {successMessage && (
                <p className="text-xl text-green-500">{successMessage}</p>
              )}
              {errorMessage && (
                <p className="text-xl text-red-500">{errorMessage}</p>
              )}
              <button
                onClick={handleReset}
                className="mt-4 rounded bg-purple-950 px-4 py-2 font-bold text-white hover:bg-rose-400"
              >
                Reset Form
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl md:text-4xl">
                Learn More About Hair Loss?
              </h1>
              <p className="p-2 text-lg md:text-lg">
                Get a free Hair Loss Survival Guide today.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="w-full rounded-lg bg-gray-100 p-6 shadow-lg">
                  <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block font-bold text-gray-700">
                        Name:
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border p-2"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block font-bold text-gray-700">
                        Email:
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border p-2"
                      />
                    </div>
                  </div>
                  <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block font-bold text-gray-700">
                        Phone:
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border p-2"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block font-bold text-gray-700">
                        City:
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border p-2"
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="mb-2 block font-bold text-gray-700">
                      Are you suffering with:
                    </label>
                    <select
                      name="suffering"
                      value={formData.suffering}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border p-2"
                    >
                      <option value="">--Please choose an option--</option>
                      <option value="hair_fall">Hair fall</option>
                      <option value="baldness">Baldness</option>
                      <option value="info">Only for information</option>
                    </select>
                  </div>
                </div>
                <span className="py-4 text-xs">
                  By submitting this form, I agree to let Trichos contact me via
                  phone, text, email, or mail about appointments, services, or
                  offers. This site is protected by reCAPTCHA and the Google.
                  <Link
                    className="px-2 text-blue-600"
                    href="https://policies.google.com/privacy"
                  >
                    Privacy Policy
                  </Link>
                  and
                  <Link
                    className="px-2 text-blue-600"
                    href="https://policies.google.com/terms"
                  >
                    Terms of Service
                  </Link>
                  apply.
                </span>
                <div className="group relative mt-4 flex justify-center self-start rounded-sm bg-purple-950 px-4 py-2 hover:bg-rose-400">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-4 py-2 font-bold text-white ${
                      loading
                        ? "cursor-not-allowed bg-gray-500"
                        : "hover:text-black"
                    }`}
                  >
                    {loading ? "Submitting..." : "Send Me the Survival Guide"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="relative flex items-end md:h-full md:w-1/2">
          <Image
            src={ebook}
            alt="Ebook Download Survival Guide"
            height={600}
            width={600}
            className="md:rounded-xl"
          />
        </div>
      </div>
    </section>
  );
}
